<template>
    <div>
        <div class="pb-4">
            <div class="flex items-center gap-2">
                <div>
                    <div class="text-xs uppercase text-gray-500">
                        {{
                            localizer.translate(
                                TextTokenEnum.BookingForm_Currency,
                            )
                        }}
                    </div>
                    <div>
                        <SelectInput
                            :useBookingFormThemeCssVariables="true"
                            :name="
                                RequestBodyKeyEnum.ScheduleMeeting_ServicePriceId
                            "
                            :value="selectedServicePrice?.id.value"
                            @update:modelValue="
                                handleServicePriceSelectionChange($event)
                            "
                        >
                            <template
                                v-for="servicePrice in serviceAggregate.servicePrices"
                                :key="servicePrice.id.value"
                            >
                                <option :value="servicePrice.id.value">
                                    {{ servicePrice.price.currency }}
                                </option>
                            </template>
                        </SelectInput>
                    </div>
                </div>
                <div>
                    <div class="text-xs uppercase text-gray-500">
                        {{
                            localizer.translate(TextTokenEnum.BookingForm_Total)
                        }}
                    </div>
                    <div
                        v-if="totalPrice"
                        class="my-1 py-px"
                    >
                        <span class="text-2xl py-2 font-semibold text-dark">
                            {{ totalPrice.getValueAsDecimal() }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-6">
            <div
                ref="paypalButtonsContainer"
                class="space-y-3"
            ></div>
        </div>

        <template
            v-if="
                serviceAggregate.service.isPaymentRequired &&
                serviceAggregate.integration?.provider ===
                    IntegrationProviderEnum.PayPalMultiParty
            "
        >
            <input
                class="optional"
                type="hidden"
                :name="RequestBodyKeyEnum.ScheduleMeeting_PayPal_OrderId"
                :value="payPalOrderId"
            />
        </template>

        <div>
            <div
                v-if="turnstileEnabled"
                class="my-4 flex justify-end"
            >
                <div
                    class="cf-turnstile"
                    :data-sitekey="turnstileSiteKey"
                ></div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, onMounted, ref, type PropType } from 'vue'
    import type {
        OneTimePaymentSession,
        OnApproveDataOneTimePayments,
        OnErrorData,
        PayPalGuestOneTimePaymentSession,
        PayPalV6Namespace,
    } from '@paypal/paypal-js/sdk-v6'
    import Company from '@/App/Company/Entity/Company'
    import DraftMeeting from '@/App/Booking/Draft/Entity/DraftMeeting'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import ServicePrice from '@/App/Service/Entity/ServicePrice'
    import Button from '@/Shared/Component/Button.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import {
        IntegrationProviderEnum,
        ResponseJsonKeyEnum,
    } from '@/Shared/AutoGenerated/Main'
    import { RoutePathBookingFormEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import useApiRequest from '@/Shared/Hook/useApiRequest'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import {
        type ConfirmBookMeetingCanScheduleMeetingCallback,
        type ConfirmBookMeetingOnErrorCallbackParams,
        type ConfirmBookMeetingOnScheduleMeetingRequested,
        type ConfirmBookMeetingOnStartCallbackParams,
    } from '@/Shared/AutoGenerated/Types'
    import type LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'

    declare global {
        interface Window {
            paypal?: PayPalV6Namespace
        }
    }

    const emits = defineEmits<{
        onStart: [ConfirmBookMeetingOnStartCallbackParams]
        onError: [ConfirmBookMeetingOnErrorCallbackParams]
        onScheduleMeetingRequested: [
            ConfirmBookMeetingOnScheduleMeetingRequested,
        ]
    }>()

    const props = defineProps({
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        draftMeeting: {
            type: Object as PropType<DraftMeeting>,
            required: true,
        },
        serviceAggregate: {
            type: Object as PropType<ServiceAggregate>,
            required: true,
        },
        turnstileEnabled: {
            type: Boolean,
            required: true,
        },
        turnstileSiteKey: {
            type: String,
            required: true,
        },
        payPalClientId: {
            type: String,
            required: true,
        },
        isProcessingPayment: {
            type: Boolean,
            required: true,
        },
        numberOfRecurringMeetingsToSchedule: {
            type: Number,
            required: true,
        },
        numberOfRequestedSeats: {
            type: Number,
            required: true,
        },
        localizer: {
            type: Object as PropType<LocalizerService>,
            required: true,
        },
        canScheduleMeeting: {
            type: Function as PropType<ConfirmBookMeetingCanScheduleMeetingCallback>,
            required: true,
        },
    })

    /**
     * Service price
     */
    const savedServicePrice = props.draftMeeting.servicePriceId
        ? props.serviceAggregate.getServicePriceById(
              props.draftMeeting.servicePriceId.value,
          )
        : null
    const selectedServicePrice = ref<ServicePrice | null>(
        savedServicePrice ??
            props.serviceAggregate.getDefaultServicePrice() ??
            null,
    )
    const totalPrice = computed(() => {
        if (!selectedServicePrice.value) {
            return null
        }

        return selectedServicePrice.value.calculateTotalPrice(
            props.numberOfRecurringMeetingsToSchedule,
            props.numberOfRequestedSeats,
        )
    })

    const isConfirmButtonDisabled = computed(() => {
        return props.isProcessingPayment
    })

    const handleServicePriceSelectionChange = (
        servicePriceId: string | null,
    ) => {
        if (!servicePriceId) {
            throw new Error('Service price should not be null', {
                cause: servicePriceId,
            })
        }

        selectedServicePrice.value = props.serviceAggregate.getServicePriceById(
            parseInt(servicePriceId),
        )
    }

    /**
     * PayPal Sessions & Buttons
     */
    const paypalButtonsContainer = ref<HTMLDivElement | null>(null)
    const payPalOrderId = ref<string | null>(null)

    let paypalSession: OneTimePaymentSession | null = null
    let guestCardSession: PayPalGuestOneTimePaymentSession | null = null

    const createOrder = async (): Promise<{ orderId: string }> => {
        const url = new UrlFactory().makeBookingConfirmationSubmitUrl(
            RoutePathBookingFormEnum.Step_Confirmation_PayPal_CreateOrder,
            props.company.slug,
            props.serviceAggregate.service.slug,
            props.draftMeeting.id.value,
            props.localizer.getLocale(),
        )

        const formData = new FormData()
        if (selectedServicePrice.value) {
            formData.append(
                RequestBodyKeyEnum.ScheduleMeeting_ServicePriceId,
                selectedServicePrice.value.id.value.toString(),
            )
        }
        formData.append(
            RequestBodyKeyEnum.ScheduleMeeting_NumberOfRecurringMeetingsToSchedule,
            props.numberOfRecurringMeetingsToSchedule.toString(),
        )
        formData.append(
            RequestBodyKeyEnum.ScheduleMeeting_NumberOfRequestedSeats,
            props.numberOfRequestedSeats.toString(),
        )

        const response = await useApiRequest().post(url, formData)

        const orderId = response.getStringFromPayload(
            ResponseJsonKeyEnum.BookingForm_Confirmation_PayPal_OrderId,
        )

        payPalOrderId.value = orderId

        return { orderId }
    }

    const renderPayPalButtons = () => {
        if (!paypalButtonsContainer.value) {
            return
        }

        paypalButtonsContainer.value.innerHTML = ''

        // ---------
        // 1. PayPal Button
        // ---------

        const paypalButton = document.createElement('paypal-button')

        paypalButton.setAttribute('type', 'checkout')
        paypalButton.style.display = 'block'
        paypalButton.style.width = '100%'

        paypalButton.addEventListener('click', async () => {
            if (!props.canScheduleMeeting()) {
                return
            }

            try {
                await paypalSession?.start(
                    { presentationMode: 'auto' },
                    createOrder(),
                )
            } catch (error: any) {
                emits('onError', {
                    message:
                        'Error trying to confirm meeting, please refresh the page and try again (code 1).',
                })
            }
        })

        // ---------
        // 2. Debit / Credit Card Button
        // ---------

        const cardContainer = document.createElement(
            'paypal-basic-card-container',
        )
        cardContainer.style.display = 'block'
        cardContainer.style.width = '100%'

        const cardButton = document.createElement('paypal-basic-card-button')
        cardButton.style.display = 'block'
        cardButton.style.width = '100%'

        cardButton.addEventListener('click', async () => {
            if (!props.canScheduleMeeting()) {
                return
            }

            try {
                await guestCardSession?.start(
                    { presentationMode: 'auto' },
                    createOrder(),
                )
            } catch (error: any) {
                emits('onError', {
                    message:
                        'Error trying to confirm meeting, please refresh the page and try again (code 2).',
                })
            }
        })

        cardContainer.appendChild(cardButton)

        paypalButtonsContainer.value.appendChild(paypalButton)
        paypalButtonsContainer.value.appendChild(cardContainer)
    }

    const setupPayPal = async () => {
        if (!props.serviceAggregate.service.isPaymentRequired) {
            throw new Error('Payment is not required.')
        }

        if (!props.serviceAggregate.integration) {
            throw new Error(
                'Payment should be collected but there is no integration',
                { cause: props.serviceAggregate },
            )
        }

        if (
            props.serviceAggregate.integration.provider !==
            IntegrationProviderEnum.PayPalMultiParty
        ) {
            throw new Error(
                `Unhandled payment provider '${props.serviceAggregate.integration.provider}'`,
            )
        }

        const paypal = window.paypal

        if (!paypal) {
            throw new Error('PayPal SDK not found.')
        }

        const handleApprove = async (data: OnApproveDataOneTimePayments) => {
            payPalOrderId.value = data.orderId

            await nextTick()

            requestScheduleMeeting()
        }

        const handleCancel = () => {
            emits('onError', {
                message: null,
            })
        }

        const handleError = (error: OnErrorData) => {
            emits('onError', {
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 3).',
            })
        }

        try {
            const sdkInstance = await paypal.createInstance({
                clientId: props.payPalClientId,
                merchantId:
                    props.serviceAggregate.integration.externalId ?? undefined,
                components: [
                    'paypal-payments',
                    'paypal-guest-payments',
                ] as const,
                pageType: 'checkout',
                clientMetadataId: `DraftMeeting:${props.draftMeeting.id.value}`,
            })

            paypalSession = sdkInstance.createPayPalOneTimePaymentSession({
                onApprove: handleApprove,
                onCancel: handleCancel,
                onError: handleError,
            })

            guestCardSession =
                sdkInstance.createPayPalGuestOneTimePaymentSession({
                    onApprove: handleApprove,
                    onCancel: handleCancel,
                    onError: handleError,
                    onWarn: (warning: any) => {
                        console.warn('PayPal card payment warning:', warning)
                    },
                })

            renderPayPalButtons()
        } catch (error: any) {
            emits('onError', {
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 4).',
            })
        }
    }

    onMounted(() => {
        setupPayPal()
    })

    /**
     * Place order request (fallback via our button)
     */
    const submitPlaceOrderRequest = async () => {
        console.log('here submitPlaceOrderRequest qqq 111')

        if (!props.canScheduleMeeting()) {
            return
        }

        emits('onStart', {})

        if (!paypalSession) {
            emits('onError', {
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 5).',
            })

            return
        }

        try {
            await paypalSession.start(
                { presentationMode: 'auto' },
                createOrder(),
            )
        } catch (error: any) {
            emits('onError', {
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 6).',
            })
        }
    }

    const requestScheduleMeeting = () => {
        emits('onStart', {})
        emits('onScheduleMeetingRequested', {})
    }
</script>
